import { Link } from "react-router-dom";
import { ArrowRight, Beaker, ListChecks, ShieldCheck, SprayCan, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { LinkRow, SubHeading } from "@/components/home/parts";

/**
 * The tools part of the homepage's "Start here" section (HomeGoals renders it under the goals): four tools, the
 * beginner pages, and the blood panel referral. Price comparison left this grid on 2026-10-22: /compare is the
 * hero's second button and the action on the prices section, so the nasal spray calculator took its cell.
 *
 * Direction B (2026-10-24): each tool is a white card with a solid icon square in its own colour, as the goal tiles.
 * Static links, so it draws at full size on the first frame. The one-line description shows from 640px. The beginner
 * links are 44px chips to the same pages they always were.
 */
const TOOLS: { to: string; icon: LucideIcon; tone: string; title: string; desc: string }[] = [
  { to: "/tools/reconstitution", icon: Beaker, tone: "tone-blue", title: "Reconstitution calculator", desc: "34 presets. Handles units, mg and mL." },
  { to: "/tools/intranasal", icon: SprayCan, tone: "tone-sky", title: "Nasal spray calculator", desc: "Total mL, mg and sprays for a nasal spray pump." },
  { to: "/tools/quiz", icon: ListChecks, tone: "tone-violet", title: "Compound quiz", desc: "Three quick questions about your goal and experience." },
  { to: "/vendors/scorecard", icon: ShieldCheck, tone: "tone-green", title: "Vendor scorecard", desc: "Each vendor's score and the red flags we check." },
];

const BEGINNER = [
  { to: "/welcome/beginner", label: "Start in four steps" },
  { to: "/beginner-guide", label: "Beginner's guide" },
  { to: "/welcome/peptides", label: "Peptide hub" },
  { to: "/nootropics", label: "Nootropics hub" },
];

export default function HomeTools({ className }: { className?: string }) {
  return (
    <div className={className}>
      <SubHeading
        id="home-tools-title"
        action={
          <Link
            to="/tools"
            className="type-small -my-2.5 inline-flex min-h-11 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
          >
            All tools <ArrowRight aria-hidden className="h-4 w-4" />
          </Link>
        }
      >
        Free tools
      </SubHeading>
      <ul aria-labelledby="home-tools-title" className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {TOOLS.map(({ to, icon: Icon, tone, title, desc }) => (
          <li key={to} className="min-w-0">
            <Link
              to={to}
              className={cn("home-card home-card-link group flex h-full flex-col gap-3 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:gap-4 sm:p-5", tone)}
            >
              <span aria-hidden className="tone-square flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                <Icon className="h-5 w-5" strokeWidth={2} />
              </span>
              <span className="min-w-0">
                <span className="type-body block font-bold leading-snug text-foreground group-hover:underline group-hover:underline-offset-4">{title}</span>
                <span className="type-small mt-1 hidden text-muted-foreground sm:block">{desc}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <LinkRow label="New here" className="mt-6" links={BEGINNER} />
      {/* The blood panel referral, as it was: same link, same code. */}
      <p className="type-small mt-5 text-muted-foreground">
        Want a blood panel?{" "}
        <a
          href="https://anabolicinsights.ai/?ref=nwm2zjb"
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Order one through Anabolic Insights
        </a>{" "}
        and use code <span className="code-mark">CHONCH</span> at checkout.
      </p>
    </div>
  );
}
